// Parâmetros de uma camada WFS, lidos do próprio serviço.
//
// Até a correção 004 a tela gravava só `endpoint_url`, e toda camada WFS
// criada por ela nascia com `parametros: {}` — a primeira ingestão morria em
// "Parâmetro typeName ausente" sem dizer de onde tirar o valor. O valor está
// no `GetCapabilities`: o operador dá a URL, o back office lê o XML, e o
// `typeName` vira um select. O campo continua editável à mão, para a fonte
// que não responde ao GetCapabilities na hora do cadastro.

import { useState, type ReactNode } from "react";
import { descobrirWfs, type CapacidadesWfs } from "../api.js";

export function DescobertaWfs({
  endpoint,
  fonteId,
  parametros,
  aoMudar,
  prefixo = "wfs",
}: {
  endpoint: string;
  fonteId: string | null;
  parametros: Record<string, unknown>;
  aoMudar: (parametros: Record<string, unknown>) => void;
  /** Prefixo dos `id` dos campos, para dois formulários não colidirem. */
  prefixo?: string;
}): ReactNode {
  const [capacidades, setCapacidades] = useState<CapacidadesWfs | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);

  const typeName = typeof parametros.typeName === "string" ? parametros.typeName : "";
  const outputFormat = typeof parametros.outputFormat === "string" ? parametros.outputFormat : "";

  async function ler(): Promise<void> {
    setLendo(true);
    setErro(null);
    try {
      const c = await descobrirWfs(endpoint.trim(), fonteId);
      setCapacidades(c);
      // Um FeatureType só: não há o que escolher, e o campo já sai preenchido.
      const unico = c.tipos.length === 1 ? c.tipos[0]!.nome : typeName;
      aoMudar(aplicar(parametros, unico, c));
    } catch (e) {
      setCapacidades(null);
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setLendo(false);
    }
  }

  return (
    <>
      <div className="campo">
        <label htmlFor={`${prefixo}-typename`}>Camada do serviço (typeName)</label>
        <div className="campo-linha">
          <input
            id={`${prefixo}-typename`}
            value={typeName}
            placeholder="prefixo:nome_da_camada"
            onChange={(e) => aoMudar(aplicar(parametros, e.target.value, capacidades))}
          />
          <button
            type="button"
            className="btn"
            disabled={endpoint.trim() === "" || lendo}
            onClick={() => void ler()}
          >
            {lendo ? "Lendo…" : "Ler capacidades"}
          </button>
        </div>
        <div className="campo-ajuda">
          O serviço publica as camadas dele no <code className="mono">GetCapabilities</code>. Ler
          preenche o nome e o formato; sem isso, a ingestão pergunta à fonte a cada execução e falha
          quando há mais de uma camada.
        </div>
        {erro ? <div className="campo-ajuda campo-ajuda-erro">{erro}</div> : null}
      </div>

      {capacidades ? (
        <div className="campo">
          <label htmlFor={`${prefixo}-tipos`}>
            {capacidades.tipos.length} camada(s) no serviço
            {capacidades.versao ? ` — WFS ${capacidades.versao}` : ""}
          </label>
          <select
            id={`${prefixo}-tipos`}
            value={typeName}
            onChange={(e) => aoMudar(aplicar(parametros, e.target.value, capacidades))}
          >
            <option value="">Escolha…</option>
            {capacidades.tipos.map((t) => (
              <option key={t.nome} value={t.nome}>
                {t.nome}
                {t.titulo && t.titulo !== t.nome ? ` — ${t.titulo}` : ""}
                {t.crsPadrao ? ` (${t.crsPadrao.replace(/^urn:ogc:def:crs:/, "")})` : ""}
              </option>
            ))}
          </select>
          {capacidades.formato_sugerido === null ? (
            <div className="campo-ajuda campo-ajuda-erro">
              A fonte só serve {capacidades.formatos.join(", ") || "GML"} e este conector lê
              GeoJSON. Para serviço ArcGIS, cadastre a camada como <b>arcgis_rest</b> apontando para
              o REST (<code className="mono">…/MapServer/N</code>).
            </div>
          ) : (
            <div className="campo-ajuda">
              Formato de saída:{" "}
              <code className="mono">{outputFormat || capacidades.formato_sugerido}</code>
              {capacidades.tetoPorPagina ? ` · teto por página ${capacidades.tetoPorPagina}` : ""}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

/** Os parâmetros com `typeName`, e o que o GetCapacidades disse sobre formato e versão. */
function aplicar(
  atuais: Record<string, unknown>,
  typeName: string,
  cap: CapacidadesWfs | null,
): Record<string, unknown> {
  const p: Record<string, unknown> = { ...atuais };
  if (typeName.trim()) p.typeName = typeName.trim();
  else delete p.typeName;
  if (cap) {
    if (cap.formato_sugerido) p.outputFormat = cap.formato_sugerido;
    if (cap.versao) p.version = cap.versao;
  }
  return p;
}
